/**
 * Invite / password-reset link (/#/virkja/<token>): inspect the token, choose a password,
 * sign in, strip the token from the URL and — when running in the browser — show the
 * Home-Screen install guide before opening the app. Rendered outside the router.
 */
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, KeyRound, Link2Off, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import type { InspectTokenResponse } from '../../../shared/dto/auth';
import { ApiFailure } from '../../api/client';
import { acceptToken, inspectToken } from '../../api/auth';
import { useAuth } from '../../auth/AuthProvider';
import { InstallGuide } from '../../components/InstallGuide';
import { Button, Card, ErrorBox, Spinner, TextInput } from '../../components/ui';
import { useT } from '../../i18n';
import { isStandalone } from '../../lib/standalone';
import { AuthBrand, AuthLayout } from './AuthLayout';
import { apiErrorText } from './errors';
import { EMPTY_NEW_PASSWORD, NewPasswordFields, newPasswordOk } from './NewPasswordFields';
import { PrivacyLink } from '../privacy/PrivacyLink';

export function isInvalidToken(e: unknown): boolean {
  return (
    e instanceof ApiFailure &&
    (e.code === 'token_invalid' ||
      e.code === 'gone' ||
      e.code === 'not_found' ||
      e.code === 'validation' ||
      e.code === 'bad_request' ||
      e.status === 410 ||
      e.status === 404)
  );
}

/** Leave the link: drop the token from the address bar and load the app fresh. */
function openApp() {
  window.history.replaceState(null, '', '/');
  window.location.replace('/');
}

export function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full;
}

export function InvalidLink() {
  const t = useT();
  return (
    <AuthLayout>
      <Card className="flex flex-col items-center gap-4 px-6 py-9 text-center">
        <span className="flex size-16 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">
          <Link2Off className="size-8" aria-hidden />
        </span>
        <h1 className="text-2xl font-bold">{t('auth.accept.invalid_title')}</h1>
        <p className="text-[var(--ink-2)]">{t('auth.accept.invalid_body')}</p>
        <Button size="lg" block onClick={openApp} className="mt-2">
          {t('auth.accept.to_login')}
        </Button>
        <PrivacyLink />
      </Card>
    </AuthLayout>
  );
}

export function InstallStep({ title }: { title?: string }) {
  const t = useT();
  return (
    <AuthLayout wide>
      <div className="mb-6 flex flex-col items-center gap-3 text-center">
        <CircleCheck className="size-14 text-[var(--ok-text)]" aria-hidden />
        <h1 className="text-[28px] leading-tight font-bold tracking-tight">{title ?? t('auth.accept.done_title')}</h1>
        <p className="max-w-lg text-[18px] text-[var(--ink-2)]">{t('auth.accept.done_body')}</p>
      </div>
      <h2 className="mb-4 text-xl font-semibold">{t('auth.install.title')}</h2>
      <InstallGuide onContinue={() => window.location.replace('/')} />
    </AuthLayout>
  );
}

function PasswordForm({ token, info, onDone, onInvalid }: { token: string; info: InspectTokenResponse; onDone: () => void; onInvalid: () => void }) {
  const t = useT();
  const auth = useAuth();
  const [pw, setPw] = useState(EMPTY_NEW_PASSWORD);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const otherUser = auth.status === 'signed-in' && auth.me.email !== info.email ? auth.me.fullName : null;
  const reset = info.kind === 'reset';
  const name = firstName(info.fullName);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setTried(true);
    if (!newPasswordOk(pw)) return;
    setError(null);
    setBusy(true);
    try {
      await acceptToken(token, info.email, pw.pw, info.kdfV);
      onDone();
    } catch (err) {
      setBusy(false);
      if (isInvalidToken(err)) onInvalid();
      else setError(apiErrorText(t, err));
    }
  };

  return (
    <AuthLayout>
      <Card className="px-5 py-7 md:px-8 md:py-9">
        <AuthBrand />
        <div className="mt-6 mb-5 flex flex-col items-center gap-2 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">
            <KeyRound className="size-7" aria-hidden />
          </span>
          <h1 className="text-2xl font-bold tracking-tight">{reset ? t('auth.accept.reset_title') : t('auth.accept.welcome', { name })}</h1>
          <p className="text-[var(--ink-2)]">{reset ? t('auth.accept.reset_intro', { name }) : t('auth.accept.invite_intro')}</p>
        </div>

        {otherUser ? (
          <p className="mb-4 flex items-start gap-3 rounded-xl bg-amber-100 px-4 py-3 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
            <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
            <span>{t('auth.accept.other_user', { name: otherUser })}</span>
          </p>
        ) : null}

        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate method="post">
          <TextInput
            label={t('auth.accept.email')}
            hint={t('auth.accept.email_hint')}
            type="email"
            name="email"
            autoComplete="username"
            value={info.email}
            readOnly
            className="bg-[var(--surface-2)]"
          />
          <NewPasswordFields value={pw} onChange={setPw} tried={tried} busy={busy} />
          {error ? <ErrorBox>{error}</ErrorBox> : null}
          <Button type="submit" size="lg" block icon={KeyRound} busy={busy} className="mt-1">
            {busy ? t('auth.accept.busy') : t('auth.accept.submit')}
          </Button>
        </form>
        <div className="mt-4 flex justify-center">
          <PrivacyLink />
        </div>
      </Card>
    </AuthLayout>
  );
}

export default function AcceptTokenScreen({ token }: { token: string }) {
  const t = useT();
  // 'leaving': the password is set and the app is being reloaded (installed app) — terminal.
  const [phase, setPhase] = useState<'form' | 'invalid' | 'install' | 'leaving'>('form');
  const q = useQuery({
    queryKey: ['auth-token', token],
    queryFn: () => inspectToken(token),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
    networkMode: 'always',
    // Signing in can wipe a previous user's queries (this one included); don't re-inspect the
    // now used token once the form is done (every phase after 'form' is final).
    enabled: phase === 'form',
  });

  if (phase === 'leaving') {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-6 py-10">
          <AuthBrand />
          <Spinner label={t('common.loading')} />
        </div>
      </AuthLayout>
    );
  }
  if (phase === 'install') return <InstallStep />;
  if (phase === 'invalid' || (q.isError && isInvalidToken(q.error))) return <InvalidLink />;

  if (q.isPending) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-6 py-10">
          <AuthBrand />
          <Spinner label={t('auth.accept.checking')} />
        </div>
      </AuthLayout>
    );
  }

  if (q.isError) {
    return (
      <AuthLayout>
        <Card className="flex flex-col gap-5 px-6 py-8">
          <AuthBrand />
          <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
            {apiErrorText(t, q.error)}
          </ErrorBox>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <PasswordForm
      token={token}
      info={q.data}
      onInvalid={() => setPhase('invalid')}
      onDone={() => {
        // Leave the form first: the sign-in may have wiped this (now used) token's query, and a
        // re-render in 'form' would inspect it again and flash "Hlekkurinn er ógildur".
        const standalone = isStandalone();
        setPhase(standalone ? 'leaving' : 'install');
        // The token is single-use: remove it from the address bar and history entry.
        window.history.replaceState(null, '', '/');
        if (standalone) window.location.replace('/');
      }}
    />
  );
}
